<template>
  <div class="customer-stats-box">
    <div class="nav stats-card">
      <h2>Order Statistics</h2>

      <!-- Loading -->
      <div v-if="loading" class="stats-loading">
        <div class="spinner"></div>
        <span>Loading order statistics...</span>
      </div>

      <!-- Error -->
      <div v-else-if="error" class="stats-error">
        {{ error }}
      </div>

      <!-- Stats Content -->
      <div v-else-if="stats" class="stats-content">
        <!-- Summary Cards Row -->
        <div class="stats-summary-row">
          <div class="summary-card highlight">
            <span class="summary-label">All-Time Orders</span>
            <span class="summary-value">{{
              fmt(stats.allTime.orderCount)
            }}</span>
          </div>
          <div class="summary-card">
            <span class="summary-label">All-Time Revenue</span>
            <span class="summary-value"
              >${{ money(stats.allTime.totalAmount) }}</span
            >
          </div>
          <div class="summary-card">
            <span class="summary-label">Avg Order Value</span>
            <span class="summary-value"
              >${{ money(stats.allTime.avgOrderValue) }}</span
            >
          </div>
          <div class="summary-card subtle">
            <span class="summary-label">Customer Since</span>
            <span class="summary-value date-value">{{
              formatDate(stats.firstOrder)
            }}</span>
          </div>
        </div>

        <!-- Year Comparison -->
        <div class="year-sections">
          <!-- This Year -->
          <div class="year-block">
            <div class="year-header">
              <h3>{{ stats.thisYear.year }}</h3>
              <div class="year-badges">
                <span class="badge orders"
                  >{{ fmt(stats.thisYear.summary.orderCount) }} orders</span
                >
                <span class="badge revenue"
                  >${{ money(stats.thisYear.summary.totalAmount) }}</span
                >
              </div>
            </div>
            <table class="stats-table">
              <thead>
                <tr>
                  <th></th>
                  <th>Net Sales</th>
                  <th>Cost</th>
                  <th>Shipping</th>
                  <th>SKUs</th>
                  <th>Orders</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="row in thisYearMonthly"
                  :key="'ty-' + row.month"
                  :class="{
                    'current-month': isCurrentMonth(
                      stats.thisYear.year,
                      row.month
                    )
                  }"
                >
                  <td class="month-label">{{ monthName(row.month) }}</td>
                  <td class="number">${{ money(row.totalAmount) }}</td>
                  <td class="number">${{ money(row.productsTotal) }}</td>
                  <td class="number">${{ money(row.shippingPaid) }}</td>
                  <td class="number">{{ fmt(row.totalSkus) }}</td>
                  <td class="number">{{ fmt(row.orderCount) }}</td>
                </tr>
              </tbody>
              <tfoot>
                <tr>
                  <td class="month-label">Total</td>
                  <td class="number">
                    ${{ money(stats.thisYear.summary.totalAmount) }}
                  </td>
                  <td class="number">
                    ${{ money(stats.thisYear.summary.productsTotal) }}
                  </td>
                  <td class="number">
                    ${{ money(stats.thisYear.summary.shippingPaid) }}
                  </td>
                  <td class="number">
                    {{ fmt(stats.thisYear.summary.totalSkus) }}
                  </td>
                  <td class="number">
                    {{ fmt(stats.thisYear.summary.orderCount) }}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>

          <!-- Last Year -->
          <div class="year-block">
            <div class="year-header">
              <h3>{{ stats.lastYear.year }}</h3>
              <div class="year-badges">
                <span class="badge orders"
                  >{{ fmt(stats.lastYear.summary.orderCount) }} orders</span
                >
                <span class="badge revenue"
                  >${{ money(stats.lastYear.summary.totalAmount) }}</span
                >
              </div>
            </div>
            <table class="stats-table">
              <thead>
                <tr>
                  <th></th>
                  <th>Net Sales</th>
                  <th>Cost</th>
                  <th>Shipping</th>
                  <th>SKUs</th>
                  <th>Orders</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in lastYearMonthly" :key="'ly-' + row.month">
                  <td class="month-label">{{ monthName(row.month) }}</td>
                  <td class="number">${{ money(row.totalAmount) }}</td>
                  <td class="number">${{ money(row.productsTotal) }}</td>
                  <td class="number">${{ money(row.shippingPaid) }}</td>
                  <td class="number">{{ fmt(row.totalSkus) }}</td>
                  <td class="number">{{ fmt(row.orderCount) }}</td>
                </tr>
              </tbody>
              <tfoot>
                <tr>
                  <td class="month-label">Total</td>
                  <td class="number">
                    ${{ money(stats.lastYear.summary.totalAmount) }}
                  </td>
                  <td class="number">
                    ${{ money(stats.lastYear.summary.productsTotal) }}
                  </td>
                  <td class="number">
                    ${{ money(stats.lastYear.summary.shippingPaid) }}
                  </td>
                  <td class="number">
                    {{ fmt(stats.lastYear.summary.totalSkus) }}
                  </td>
                  <td class="number">
                    {{ fmt(stats.lastYear.summary.orderCount) }}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      </div>

      <!-- No data -->
      <div v-else class="stats-empty">
        No order data available for this customer.
      </div>
    </div>
  </div>
</template>

<script>
import { getCustomerOrderStats } from "@/api/store/CustomersService.js";

const MONTH_NAMES = [
  "",
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec"
];

export default {
  props: {
    customerId: { type: String, required: true }
  },
  data() {
    return {
      stats: null,
      loading: false,
      error: null
    };
  },
  computed: {
    thisYearMonthly() {
      return this.stats ? this.stats.thisYear.monthly : [];
    },
    lastYearMonthly() {
      return this.stats ? this.stats.lastYear.monthly : [];
    }
  },
  watch: {
    customerId: {
      immediate: true,
      handler(val) {
        if (val) this.fetchStats();
      }
    }
  },
  methods: {
    async fetchStats() {
      this.loading = true;
      this.error = null;
      try {
        this.stats = await getCustomerOrderStats(this.customerId);
      } catch (e) {
        this.error = "Failed to load order statistics.";
        console.error(e);
      } finally {
        this.loading = false;
      }
    },
    money(val) {
      const n = parseFloat(val) || 0;
      return n.toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    },
    fmt(val) {
      const n = parseInt(val) || 0;
      return n.toLocaleString("en-US");
    },
    monthName(m) {
      return MONTH_NAMES[parseInt(m)] || m;
    },
    isCurrentMonth(year, month) {
      const now = new Date();
      return (
        now.getFullYear() === year && now.getMonth() + 1 === parseInt(month)
      );
    },
    formatDate(dateStr) {
      if (!dateStr) return "—";
      const d = new Date(dateStr);
      return d.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric"
      });
    }
  }
};
</script>

<style lang="scss" scoped>
.customer-stats-box {
  width: 100%;
  font-size: 14px;
  color: #1a1a2e;
  h2 {
    font-weight: bold;
    font-size: 18px;
    margin-bottom: 12px;
  }
}
.stats-card {
  @apply p-4 border rounded-md;
  width: 100%;
}

/* Loading */
.stats-loading {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 24px;
  color: #6b7280;
  font-size: 14px;
}
.spinner {
  width: 20px;
  height: 20px;
  border: 2px solid #e5e7eb;
  border-top-color: #3b82f6;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
}
@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.stats-error {
  padding: 16px;
  color: #dc2626;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 6px;
}
.stats-empty {
  padding: 24px;
  text-align: center;
  color: #9ca3af;
}

/* Summary Cards */
.stats-summary-row {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
  margin-bottom: 20px;
  @media screen and (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media screen and (max-width: 500px) {
    grid-template-columns: 1fr;
  }
}
.summary-card {
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 14px 16px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  &.highlight {
    background: #eff6ff;
    border-color: #bfdbfe;
  }
  &.subtle .summary-value {
    font-size: 14px;
    font-weight: 500;
  }
}
.summary-label {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #6b7280;
  font-weight: 600;
}
.summary-value {
  font-size: 20px;
  font-weight: 700;
  color: #111827;
  &.date-value {
    font-size: 14px;
    font-weight: 500;
  }
}

/* Year Sections */
.year-sections {
  display: flex;
  flex-direction: column;
  gap: 20px;
}
.year-block {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  overflow: hidden;
}
.year-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 16px;
  background: #f3f4f6;
  border-bottom: 1px solid #e5e7eb;
  h3 {
    font-size: 16px;
    font-weight: 700;
    margin: 0;
    color: #111827;
  }
}
.year-badges {
  display: flex;
  gap: 8px;
}
.badge {
  font-size: 12px;
  font-weight: 600;
  padding: 3px 10px;
  border-radius: 12px;
  &.orders {
    background: #dbeafe;
    color: #1d4ed8;
  }
  &.revenue {
    background: #d1fae5;
    color: #065f46;
  }
}

/* Stats Table */
.stats-table {
  width: 100%;
  border-collapse: collapse;
  thead {
    th {
      padding: 8px 12px;
      text-align: right;
      font-size: 11px;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      color: #6b7280;
      font-weight: 600;
      border-bottom: 1px solid #e5e7eb;
      background: #fafafa;
      &:first-child {
        text-align: left;
      }
    }
  }
  tbody {
    tr {
      border-bottom: 1px solid #f3f4f6;
      transition: background 0.1s;
      &:hover {
        background: #f9fafb;
      }
      &.current-month {
        background: #eff6ff;
        font-weight: 600;
      }
    }
    td {
      padding: 7px 12px;
      font-size: 13px;
    }
  }
  tfoot {
    tr {
      background: #f3f4f6;
      border-top: 2px solid #d1d5db;
    }
    td {
      padding: 8px 12px;
      font-weight: 700;
      font-size: 13px;
    }
  }
  .month-label {
    font-weight: 600;
    color: #374151;
    text-align: left;
  }
  .number {
    text-align: right;
    font-variant-numeric: tabular-nums;
    color: #1f2937;
  }
}

/* Mobile: scroll tables horizontally */
@media screen and (max-width: 768px) {
  .year-block {
    overflow-x: auto;
  }
  .stats-table {
    min-width: 480px;
  }
}
</style>
